import { jwtDecode, JwtPayload } from 'jwt-decode';
import getUrlSearchParam from './urlSearchParams.helper';

type PaymentMethod = 'on_account' | 'credit_card' | 'braintree' | 'pci_pal';

interface CustomJwtPayload extends JwtPayload {
  source?: string;
  email?: string;
  iss?: string;
  provider?: string;
  paymentMethod?: string;
}

interface authHelper {
  getRawToken: () => string | null;
  getToken: () => CustomJwtPayload | null;
  setToken: (token: string) => void;
  isAuthCustomer: () => boolean;
  isAnonCustomer: () => boolean;
  isAgent: () => boolean;
  availablePaymentMethods: () => PaymentMethod[];
}

let rawToken: string | null = null;
let urlParams: URLSearchParams | null;

const authHelper = (): authHelper => {
  const getRawToken = (): string | null => {
    if (rawToken) {
      return rawToken;
    }

    if (!urlParams) urlParams = new URLSearchParams(window.location.search);

    const tokenFromUrl = getUrlSearchParam('token', urlParams);

    if (tokenFromUrl) {
      rawToken = tokenFromUrl;
    } else {
      rawToken =
        localStorage.getItem('token') ?? localStorage.getItem('anonymousToken');
    }

    return rawToken ?? null;
  };

  const setToken = (token: string): void => {
    rawToken = token;
  };

  const getToken = (): CustomJwtPayload | null => {
    const token = getRawToken();
    if (!token) {
      return null;
    }
    return jwtDecode(token);
  };

  const isAuthCustomer = (): boolean => {
    const token: CustomJwtPayload | null = getToken();
    if (!token) {
      return false;
    }
    return token.iss?.includes('cognito') ?? false;
  };

  const isAnonCustomer = (): boolean => {
    const token: CustomJwtPayload | null = getToken();
    if (!token) {
      return false;
    }
    return token?.source === 'CM';
  };

  const isAgent = (): boolean => {
    const token = getToken();

    if (!token) {
      return false;
    }
    return !isAuthCustomer() && !isAnonCustomer();
  };

  const availablePaymentMethods = (): PaymentMethod[] => {
    if (urlParams) {
      // TODO: remove this, it's only for Indep UAT phase
      const paymentMethod = getUrlSearchParam('paymentMethod', urlParams);
      if (paymentMethod) {
        return [paymentMethod as PaymentMethod];
      }
    }

    const token = getToken();
    if (!token || !token.paymentMethod) {
      return [];
    }
    return [token.paymentMethod as PaymentMethod];
  };

  return {
    getRawToken,
    getToken,
    setToken,
    isAuthCustomer,
    isAnonCustomer,
    isAgent,
    availablePaymentMethods,
  };
};

export default authHelper;
